import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Leaf,
  LogOut,
  Package,
  Newspaper,
  BarChart3,
  Phone,
  MapPin,
  Trash2,
  Plus,
  Pencil,
  X,
  Loader2,
  TreePine,
  Recycle,
  Users,
  Upload,
  MessageSquare,
  Bell,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  pending: "bg-terracotta/15 text-terracotta border-terracotta/30",
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  collected: "bg-forest/15 text-forest border-forest/30",
};

function StatTile({ icon: Icon, value, label }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-sage flex items-center justify-center">
          <Icon className="h-5 w-5 text-forest-dark" />
        </div>
        <div>
          <div className="text-2xl font-serif text-forest-dark leading-none">{value}</div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_POST = { title: "", body: "", category: "Tree Plantation", location: "", image_url: "", published: true };
const POST_CATEGORIES = ["Tree Plantation", "Recycling Project", "Community Drive", "Awareness", "Activity"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loadingDonations, setLoadingDonations] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState(null); // {type, id}
  const [postDialog, setPostDialog] = useState(false);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [editingPostId, setEditingPostId] = useState(null);
  const [savingPost, setSavingPost] = useState(false);

  const [impactForm, setImpactForm] = useState({
    trees_planted: 0,
    waste_diverted_kg: 0,
    projects_completed: 0,
    volunteers: 0,
  });
  const [savingImpact, setSavingImpact] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({ name: "", phone: "", sms: true, whatsapp: true });
  const [addingRecipient, setAddingRecipient] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const loadStats = useCallback(() => {
    api.get("/stats").then((r) => {
      setStats(r.data);
      setImpactForm(r.data.impact);
    });
  }, []);

  const loadDonations = useCallback(() => {
    setLoadingDonations(true);
    api
      .get("/donations", { params: { status: statusFilter } })
      .then((r) => setDonations(r.data))
      .finally(() => setLoadingDonations(false));
  }, [statusFilter]);

  const loadPosts = useCallback(() => {
    api.get("/admin/posts").then((r) => setPosts(r.data));
  }, []);

  const loadRecipients = useCallback(() => {
    api.get("/admin/recipients").then((r) => setRecipients(r.data));
  }, []);

  useEffect(() => {
    loadStats();
    loadPosts();
    loadRecipients();
  }, [loadStats, loadPosts, loadRecipients]);

  useEffect(() => {
    loadDonations();
  }, [loadDonations]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/donations/${id}/status`, { status });
      toast.success("Status updated");
      loadDonations();
      loadStats();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "donation") {
        await api.delete(`/donations/${deleteTarget.id}`);
        loadDonations();
        loadStats();
      } else if (deleteTarget.type === "post") {
        await api.delete(`/admin/posts/${deleteTarget.id}`);
        loadPosts();
      } else if (deleteTarget.type === "recipient") {
        await api.delete(`/admin/recipients/${deleteTarget.id}`);
        loadRecipients();
      }
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setDeleteTarget(null);
    }
  };

  const addRecipient = async () => {
    if (!newRecipient.phone.trim()) {
      toast.error("Enter a phone number in international format, e.g. +9198...");
      return;
    }
    setAddingRecipient(true);
    try {
      await api.post("/admin/recipients", newRecipient);
      toast.success("Recipient added");
      setNewRecipient({ name: "", phone: "", sms: true, whatsapp: true });
      loadRecipients();
    } catch (e) {
      toast.error("Could not add recipient");
    } finally {
      setAddingRecipient(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setPostForm((prev) => ({ ...prev, image_url: fullUrl }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openNewPost = () => {
    setPostForm(EMPTY_POST);
    setEditingPostId(null);
    setPostDialog(true);
  };

  const openEditPost = (p) => {
    setPostForm({
      title: p.title,
      body: p.body,
      category: p.category,
      location: p.location || "",
      image_url: p.image_url || "",
      published: p.published,
    });
    setEditingPostId(p.id);
    setPostDialog(true);
  };

  const savePost = async () => {
    if (!postForm.title.trim() || !postForm.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSavingPost(true);
    try {
      if (editingPostId) {
        await api.put(`/admin/posts/${editingPostId}`, postForm);
      } else {
        await api.post("/admin/posts", postForm);
      }
      toast.success("Post saved");
      setPostDialog(false);
      loadPosts();
    } catch (e) {
      toast.error("Could not save post");
    } finally {
      setSavingPost(false);
    }
  };

  const saveImpact = async () => {
    setSavingImpact(true);
    try {
      await api.put("/admin/impact", {
        trees_planted: Number(impactForm.trees_planted) || 0,
        waste_diverted_kg: Number(impactForm.waste_diverted_kg) || 0,
        projects_completed: Number(impactForm.projects_completed) || 0,
        volunteers: Number(impactForm.volunteers) || 0,
      });
      toast.success("Impact numbers updated");
      loadStats();
    } catch (e) {
      toast.error("Could not save impact");
    } finally {
      setSavingImpact(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-bone">
      <header className="sticky top-0 z-40 bg-forest-dark text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-forest flex items-center justify-center">
              <Leaf className="h-4 w-4 text-white" />
            </div>
            <span className="font-serif text-xl">Paryavaran Suraksha Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/70" data-testid="admin-email">
              {user?.email}
            </span>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="outline"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-forest-dark"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatTile icon={Package} value={stats ? stats.total_donations : "—"} label="Total donations" />
          <StatTile icon={BarChart3} value={stats ? stats.pending_donations : "—"} label="Awaiting pickup" />
          <StatTile icon={TreePine} value={stats ? stats.impact.trees_planted : "—"} label="Trees planted" />
          <StatTile icon={Recycle} value={stats ? `${stats.impact.waste_diverted_kg} kg` : "—"} label="Waste diverted" />
        </div>

        <Tabs defaultValue="donations">
          <TabsList className="bg-white border border-stone-200 rounded-2xl sm:rounded-full p-1 flex flex-wrap h-auto gap-1">
            <TabsTrigger value="donations" data-testid="tab-donations" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" /> Donations
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white">
              <Newspaper className="h-4 w-4 mr-2" /> Activities
            </TabsTrigger>
            <TabsTrigger value="impact" data-testid="tab-impact" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" /> Impact
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white">
              <Bell className="h-4 w-4 mr-2" /> Alerts
            </TabsTrigger>
          </TabsList>

          {/* Donations tab */}
          <TabsContent value="donations" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-serif text-forest-dark">Donation requests</h2>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-44 rounded-full bg-white border-stone-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="collected">Collected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loadingDonations ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-forest" />
              </div>
            ) : donations.length === 0 ? (
              <div data-testid="no-donations" className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center text-muted-foreground">
                No donation requests yet.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="donations-list">
                {donations.map((d) => (
                  <div key={d.id} data-testid={`donation-card-${d.id}`} className="bg-white border border-stone-200 rounded-2xl p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-serif text-forest-dark">{d.donor_name}</h3>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <Phone className="h-3.5 w-3.5" /> {d.contact_number}
                        </div>
                        <div className="flex items-start gap-1 text-sm text-muted-foreground mt-1">
                          <MapPin className="h-3.5 w-3.5 mt-0.5" /> {d.pickup_location}
                        </div>
                      </div>
                      <Badge className={`capitalize border ${STATUS_STYLES[d.status]}`}>{d.status}</Badge>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {d.items.map((it, i) => (
                        <span key={i} className="text-xs bg-sage/60 text-forest-dark rounded-full px-3 py-1">
                          {it.category}: <b>{it.quantity}</b>
                        </span>
                      ))}
                    </div>

                    {d.notes && <p className="text-sm text-muted-foreground mt-3 italic">"{d.notes}"</p>}

                    <div className="flex items-center gap-2 mt-5 pt-4 border-t border-stone-100">
                      <Select value={d.status} onValueChange={(v) => changeStatus(d.id, v)}>
                        <SelectTrigger data-testid={`status-select-${d.id}`} className="w-40 rounded-full bg-bone border-stone-300 h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="collected">Collected</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-donation-${d.id}`}
                        onClick={() => setDeleteTarget({ type: "donation", id: d.id })}
                        className="text-terracotta hover:bg-terracotta/10 rounded-full ml-auto"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Posts tab */}
          <TabsContent value="posts" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-serif text-forest-dark">Activity posts</h2>
              <Button onClick={openNewPost} data-testid="new-post-btn" className="rounded-full bg-forest hover:bg-forest-dark text-white">
                <Plus className="h-4 w-4 mr-1" /> New post
              </Button>
            </div>

            {posts.length === 0 ? (
              <div data-testid="no-admin-posts" className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center text-muted-foreground">
                No activity posts yet. Create one to share your latest work.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-posts-list">
                {posts.map((p) => (
                  <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-sage flex items-center justify-center">
                        <Leaf className="h-8 w-8 text-forest/40" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-terracotta">{p.category}</span>
                        {!p.published && <Badge variant="outline" className="text-[10px]">Draft</Badge>}
                      </div>
                      <h3 className="text-lg font-serif text-forest-dark leading-snug">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.body}</p>
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" data-testid={`edit-post-${p.id}`} onClick={() => openEditPost(p)} className="rounded-full border-forest text-forest hover:bg-forest hover:text-white">
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-post-${p.id}`} onClick={() => setDeleteTarget({ type: "post", id: p.id })} className="rounded-full text-terracotta hover:bg-terracotta/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Impact tab */}
          <TabsContent value="impact" className="mt-6">
            <h2 className="text-2xl font-serif text-forest-dark mb-4">Impact counters</h2>
            <div className="bg-white border border-stone-200 rounded-2xl p-8 max-w-2xl">
              <p className="text-sm text-muted-foreground mb-6">These numbers appear on the public homepage.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { key: "trees_planted", label: "Trees planted", icon: TreePine },
                  { key: "waste_diverted_kg", label: "Waste diverted (kg)", icon: Recycle },
                  { key: "projects_completed", label: "Projects completed", icon: Package },
                  { key: "volunteers", label: "Volunteers", icon: Users },
                ].map((f) => (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark flex items-center gap-2">
                      <f.icon className="h-3.5 w-3.5" /> {f.label}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid={`impact-${f.key}`}
                      value={impactForm[f.key]}
                      onChange={(e) => setImpactForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="rounded-xl border-stone-300 bg-bone focus-visible:ring-forest"
                    />
                  </div>
                ))}
              </div>
              <Button
                onClick={saveImpact}
                disabled={savingImpact}
                data-testid="save-impact-btn"
                className="mt-6 rounded-full bg-forest hover:bg-forest-dark text-white transition-transform hover:-translate-y-0.5"
              >
                {savingImpact ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save impact numbers"}
              </Button>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-8 mt-6 max-w-3xl" data-testid="category-chart-card">
              <h3 className="text-xl font-serif text-forest-dark mb-1">Donations by category</h3>
              <p className="text-sm text-muted-foreground mb-6">Live breakdown of every item pledged by donors.</p>
              {stats && Object.keys(stats.category_counts || {}).length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={Object.entries(stats.category_counts).map(([name, count]) => ({ name, count }))}
                    margin={{ top: 8, right: 8, left: -20, bottom: 40 }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                      tick={{ fontSize: 11, fill: "#5a5a52" }}
                      height={60}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#5a5a52" }} />
                    <Tooltip
                      cursor={{ fill: "rgba(45,106,79,0.08)" }}
                      contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontFamily: "DM Sans" }}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {Object.keys(stats.category_counts).map((_, i) => (
                        <Cell key={i} fill={["#2D6A4F", "#40916C", "#A95C3F", "#74A892", "#B08968", "#52796F"][i % 6]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-muted-foreground py-12" data-testid="chart-empty">
                  No donation data yet to chart.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Alerts tab */}
          <TabsContent value="alerts" className="mt-6">
            <h2 className="text-2xl font-serif text-forest-dark mb-1">Notification recipients</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
              These admins get an SMS and WhatsApp alert whenever a new donation is submitted. Use full
              international format (e.g. +9198...). For WhatsApp via the Twilio sandbox, each recipient must
              first message the sandbox join code once.
            </p>

            <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 max-w-3xl" data-testid="add-recipient-card">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Name</Label>
                  <Input
                    data-testid="recipient-name-input"
                    value={newRecipient.name}
                    onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                    className="rounded-xl border-stone-300 bg-bone"
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Phone (E.164)</Label>
                  <Input
                    data-testid="recipient-phone-input"
                    value={newRecipient.phone}
                    onChange={(e) => setNewRecipient({ ...newRecipient, phone: e.target.value })}
                    className="rounded-xl border-stone-300 bg-bone"
                    placeholder="+9198XXXXXXXX"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <label className="flex items-center gap-2 text-sm text-forest-dark cursor-pointer">
                  <input type="checkbox" data-testid="recipient-sms-checkbox" checked={newRecipient.sms} onChange={(e) => setNewRecipient({ ...newRecipient, sms: e.target.checked })} className="accent-forest h-4 w-4" />
                  <MessageSquare className="h-4 w-4" /> SMS
                </label>
                <label className="flex items-center gap-2 text-sm text-forest-dark cursor-pointer">
                  <input type="checkbox" data-testid="recipient-whatsapp-checkbox" checked={newRecipient.whatsapp} onChange={(e) => setNewRecipient({ ...newRecipient, whatsapp: e.target.checked })} className="accent-forest h-4 w-4" />
                  <Phone className="h-4 w-4" /> WhatsApp
                </label>
                <Button
                  onClick={addRecipient}
                  disabled={addingRecipient}
                  data-testid="add-recipient-btn"
                  className="ml-auto rounded-full bg-forest hover:bg-forest-dark text-white transition-transform hover:-translate-y-0.5"
                >
                  {addingRecipient ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-1" /> Add recipient</>)}
                </Button>
              </div>
            </div>

            {recipients.length === 0 ? (
              <div data-testid="no-recipients" className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center text-muted-foreground max-w-3xl">
                No recipients yet. Add a phone number to start receiving alerts.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl" data-testid="recipients-list">
                {recipients.map((r) => (
                  <div key={r.id} data-testid={`recipient-card-${r.id}`} className="bg-white border border-stone-200 rounded-2xl p-5 flex items-start justify-between">
                    <div>
                      <div className="font-serif text-lg text-forest-dark">{r.name || "Admin"}</div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Phone className="h-3.5 w-3.5" /> {r.phone}
                      </div>
                      <div className="flex gap-2 mt-3">
                        {r.sms && <span className="text-[10px] uppercase tracking-[0.15em] bg-sage/60 text-forest-dark rounded-full px-2 py-0.5">SMS</span>}
                        {r.whatsapp && <span className="text-[10px] uppercase tracking-[0.15em] bg-sage/60 text-forest-dark rounded-full px-2 py-0.5">WhatsApp</span>}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-recipient-${r.id}`}
                      onClick={() => setDeleteTarget({ type: "recipient", id: r.id })}
                      className="text-terracotta hover:bg-terracotta/10 rounded-full"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Post dialog */}
      <Dialog open={postDialog} onOpenChange={setPostDialog}>
        <DialogContent className="bg-bone max-w-lg" data-testid="post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest-dark">
              {editingPostId ? "Edit post" : "New activity post"}
            </DialogTitle>
            <DialogDescription>
              Share an environment activity with visitors on the public homepage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Title</Label>
              <Input data-testid="post-title-input" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} className="rounded-xl border-stone-300 bg-white" placeholder="e.g. 500 saplings planted in Riverside" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Category</Label>
                <Select value={postForm.category} onValueChange={(v) => setPostForm({ ...postForm, category: v })}>
                  <SelectTrigger data-testid="post-category-select" className="rounded-xl border-stone-300 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POST_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Location</Label>
                <Input data-testid="post-location-input" value={postForm.location} onChange={(e) => setPostForm({ ...postForm, location: e.target.value })} className="rounded-xl border-stone-300 bg-white" placeholder="Optional" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Photo</Label>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="post-image-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="upload-image-btn"
                  className="rounded-full border-forest text-forest hover:bg-forest hover:text-white"
                >
                  {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                  {uploadingImage ? "Uploading..." : "Upload photo"}
                </Button>
                {postForm.image_url && (
                  <img
                    src={postForm.image_url}
                    alt="preview"
                    data-testid="post-image-preview"
                    className="h-12 w-12 rounded-lg object-cover border border-stone-200"
                  />
                )}
              </div>
              <Input
                data-testid="post-image-input"
                value={postForm.image_url}
                onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })}
                className="rounded-xl border-stone-300 bg-white"
                placeholder="...or paste an image URL"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Description</Label>
              <Textarea data-testid="post-body-input" value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} className="rounded-xl border-stone-300 bg-white min-h-[110px]" placeholder="Describe the activity..." />
            </div>
            <label className="flex items-center gap-2 text-sm text-forest-dark cursor-pointer">
              <input type="checkbox" data-testid="post-published-checkbox" checked={postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} className="accent-forest h-4 w-4" />
              Publish to public site
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostDialog(false)} className="rounded-full">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={savePost} disabled={savingPost} data-testid="save-post-btn" className="rounded-full bg-forest hover:bg-forest-dark text-white">
              {savingPost ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-bone" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-forest-dark">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this {deleteTarget?.type}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-btn" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
